/**
 * makeLabel — desenho do rótulo do jogador ("Nome" ou "Nome: mensagem").
 *
 * Este módulo só MEDE e PINTA a pílula num contexto 2D. Quem decide onde ela vai
 * parar são dois consumidores:
 *   • LabelAtlas — o caminho normal: a pílula é pintada numa célula de um atlas
 *     compartilhado e TODOS os jogadores saem numa única chamada de desenho;
 *   • o sprite individual daqui (makeDynamicLabel) — usado só quando o rótulo NÃO
 *     cabe numa célula do atlas: enquanto o jogador exibe uma mensagem (o texto
 *     cresce caractere a caractere) ou quando o nome é comprido demais.
 *
 * Regras de quebra:
 *   • o rótulo cresce horizontalmente até MAXW; acima disso, quebra em ESPAÇOS
 *     (palavras nunca quebram no meio). Logo, um nome de 1 palavra nunca vira 2
 *     linhas; "Francisco Xavier" pode virar "Francisco" / "Xavier"; e a mensagem
 *     longa também quebra dentro da largura máxima.
 *   • se UMA linha (uma palavra única muito longa) ainda passa de MAXW, o texto
 *     é encolhido horizontalmente para caber (mantém a proporção na tela).
 *   • a ALTURA para em MAX_LINES (a última ganha reticências) e o texto é cortado
 *     em NAME_MAX/MSG_MAX caracteres ANTES de ser montado — ver abaixo.
 *
 * POR QUE HÁ DOIS TETOS (linhas e caracteres). A largura era limitada e o número de
 * linhas não: um `say` de 300 caracteres (colagem comum de WhatsApp) virava uma
 * pílula de 55 unidades de MUNDO de altura, e um de 2.000 passava de 340 — os
 * prédios da cidade têm de 11 a 20. Como o rótulo desenha com `depthTest:false` e
 * `renderOrder` 999, UMA mensagem longa tapava o telão inteiro para todos os
 * espectadores daquela cidade. Os dois tetos atacam pontas diferentes do problema:
 *   • MAX_LINES limita a GEOMETRIA — a pílula para em 7,7 unidades de mundo (9,2 já
 *     com a escala do avatar), e o canvas do sprite para numa classe de 256×128 px
 *     (era 256×4416 e 4,3 MB de VRAM no teste, e com as métricas reais da fonte
 *     chegava a 8128 px — a um passo do MAX_TEXTURE_SIZE de 8192 das integradas).
 *   • NAME_MAX/MSG_MAX limitam o TEMPO e o TRABALHO — a mensagem é digitada
 *     caractere a caractere, então 2.000 caracteres eram 149 s de tela e 70
 *     recriações de textura. Cortar antes de montar também poupa a quebra de linha,
 *     que roda a cada caractere digitado.
 * O servidor também tem teto, mas o front precisa se defender sozinho (servidor
 * antigo, ou mensagem já em trânsito quando o teto subiu).
 *
 * RESOLUÇÃO (LABEL_RES): o rótulo era desenhado a 44px de fonte e exibido, na
 * vista padrão, com ~161 pixels de largura na tela — ou seja, 2,3× mais textura
 * do que a tela consegue mostrar, e 5,2× em área. Isso custava memória de vídeo e
 * banda à toa, e num atlas custaria células enormes. Agora TODAS as medidas em px
 * são multiplicadas por LABEL_RES e a conversão px→mundo é dividida por ele: o
 * rótulo tem exatamente o mesmo tamanho em unidades de mundo, só é rasterizado
 * mais perto de 1:1 com a tela.
 *
 * CANVAS QUE SÓ CRESCE (caminho sprite): trocar `canvas.width/height` INVALIDA a
 * textura na GPU — o three destrói e recria (glDeleteTexture + glTexImage2D
 * inteiro). Como a mensagem é digitada caractere a caractere (~22 quadros de
 * texto por segundo), o canvas mudava de largura quase todo caractere e a textura
 * ia junto. Arredondando para CLASSES de tamanho e nunca encolhendo, ele
 * estabiliza em 1–2 recriações e daí em diante só re-envia os pixels.
 *
 * QUAD JUSTO NA PÍLULA: a sobra da classe de tamanho é margem transparente. Se o
 * quad cobrisse o canvas inteiro, essa margem viraria pixel rasterizado e
 * misturado (alpha zero, mas fragment shader e blend rodam igual). Então o quad
 * tem o tamanho da PÍLULA e o recorte sai na textura (`offset`/`repeat`).
 */
import * as THREE from 'three';

/** Resolução de rasterização do rótulo (1 = o valor histórico de 44px de fonte). */
export const LABEL_RES = 0.6;

const MAXW = 360 * LABEL_RES; // largura máxima do conteúdo (px)
const PAD = 16 * LABEL_RES;
const FONT = 44 * LABEL_RES;
const LINE_H = (44 + 10) * LABEL_RES;
const RADIUS = 18 * LABEL_RES; // raio da pílula
const STROKE = 2 * LABEL_RES;  // espessura da borda
const FONT_CSS = `700 ${FONT}px Inter, sans-serif`;
/** px do canvas → unidades de mundo. Dividido por LABEL_RES para o tamanho FINAL
 *  em mundo não mudar quando a resolução de rasterização muda. */
export const LABEL_SCALE = (0.022 * 1.8) / LABEL_RES;
const STEP = 64; // granularidade das classes de tamanho do canvas do sprite (px)

/** Teto de LINHAS da pílula (a última sai com reticências). 3 é o maior valor que
 *  mantém a pílula mais baixa que o prédio mais baixo da cidade (11u). */
export const MAX_LINES = 3;
/** Teto de caracteres do NOME. Nome vem do parceiro pelo /ingress — um "nome" de
 *  200 caracteres estraga o telão igual a um `say` gigante. */
export const NAME_MAX = 32;
/** Teto de caracteres da MENSAGEM (`say`/emote). Calibrado pela GEOMETRIA, não por
 *  gosto: 3 linhas de MAXW comportam de 27 (glifos largos) a 44 (estreitos)
 *  caracteres — acima disso a pílula já sairia com reticências, então digitar mais
 *  só gastaria tempo de telão (45 ms por caractere) sem mostrar nada a mais. 72 é a
 *  margem para textos estreitos (muitos "i"/"l"/espaços) e emoji. */
export const MSG_MAX = 72;
const ELLIPSIS = '…';
const TRAILING_WS = /\s+$/; // fora da função: literal de regex aloca a cada avaliação

// contexto auxiliar só para medir largura de texto (independe do canvas real)
const _measure = document.createElement('canvas').getContext('2d');
// resultado de measureLabel reaproveitado (chamado por spawn e por caractere digitado)
const _metrics = { w: 0, h: 0, lines: [], fit: 1 };

/**
 * Mede a pílula de `text`: largura/altura em px de canvas, linhas já quebradas e
 * o fator de compressão horizontal. O objeto devolvido é REAPROVEITADO — use ou
 * copie antes da próxima chamada.
 */
export function measureLabel(text) {
  _measure.font = FONT_CSS;
  const lines = wrap(_measure, text, MAXW, MAX_LINES);
  let widest = 0;
  for (const l of lines) widest = Math.max(widest, _measure.measureText(l).width);
  _metrics.fit = widest > MAXW ? MAXW / widest : 1; // palavra única longa → encolhe
  _metrics.w = Math.ceil(Math.min(widest, MAXW)) + PAD * 2;
  _metrics.h = lines.length * LINE_H + PAD * 2;
  _metrics.lines = lines;
  return _metrics;
}

/**
 * Pinta a pílula com o canto superior esquerdo em (ox, oy) do contexto `ctx`.
 * `m` é o retorno de measureLabel para o mesmo texto.
 */
export function paintLabel(ctx, ox, oy, m, color) {
  const { w, h, lines, fit } = m;
  ctx.fillStyle = 'rgba(6,18,11,0.72)';
  roundRect(ctx, ox + 1, oy + 1, w - 2, h - 2, RADIUS);
  ctx.fill();
  ctx.strokeStyle = 'rgba(61,255,134,0.4)';
  ctx.lineWidth = STROKE;
  roundRect(ctx, ox + 1, oy + 1, w - 2, h - 2, RADIUS);
  ctx.stroke();
  // texto centralizado (com escala horizontal p/ caber palavra única longa)
  ctx.font = FONT_CSS;
  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.save();
  ctx.translate(ox + w / 2, oy + PAD + LINE_H / 2);
  ctx.scale(fit, 1);
  for (let i = 0; i < lines.length; i++) ctx.fillText(lines[i], 0, i * LINE_H);
  ctx.restore();
}

/**
 * Corta `s` em `max` caracteres, com reticências, SEM nunca passar de `max` (as
 * reticências ocupam o último lugar). Conta PONTOS DE CÓDIGO, não unidades UTF-16:
 * emoji e acentos compostos ocupam 2 unidades, e cortar por `.length` partiria o par
 * substituto — o glifo vira o quadradinho "tofu" no meio do rótulo.
 * O caminho rápido (`t.length <= max`) cobre todo nome normal sem alocar o array.
 */
export function clipChars(s, max) {
  const t = String(s ?? '');
  if (t.length <= max) return t;
  const cp = [...t];
  return cp.length <= max ? t : cp.slice(0, max - 1).join('') + ELLIPSIS;
}

/** Monta o texto exibido: "Nome" ou "Nome: sufixo", já com os tetos aplicados. */
export function labelText(name, suffix) {
  const n = clipChars(name, NAME_MAX);
  return suffix != null && suffix !== '' ? `${n}: ${clipChars(suffix, MSG_MAX)}` : n;
}

/* =========================== caminho SPRITE (fallback) =========================== */

/** Cria um rótulo dinâmico (canvas/textura/sprite próprios). */
export function makeDynamicLabel() {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 8; // redimensionado no 1º draw
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  // MESMA escolha do atlas (ver LabelAtlas._newPage): rótulo é billboard chapado, sempre
  // visto de frente, então mipmap não melhora nada — e custa. Ligado, o three regerava a
  // pirâmide inteira a CADA repintura da textura: um jogador digitando repinta ~15×/s, e
  // ainda somava +33% de VRAM por sprite. Este é o caminho de exceção (mensagem em
  // digitação), justamente o que mais repinta.
  tex.generateMipmaps = false;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  // chapado como o atlas (ver LabelAtlas): sem fog e sem tone mapping
  const mat = new THREE.SpriteMaterial({
    map: tex, transparent: true, depthWrite: false, depthTest: false,
    fog: false, toneMapped: false
  });
  const sprite = new THREE.Sprite(mat);
  // tamanho da PÍLULA em unidades de mundo — é a escala do sprite E a caixa usada
  // pelo de-clutter e pela linha conectora (a margem sai pelo recorte da textura)
  sprite.userData.base = { x: 1, y: 1 };
  return { sprite, canvas, tex, mat, _key: '' };
}

/**
 * Devolve o rótulo ao estado "recém-criado" para ser REUSADO por outro jogador
 * (ver o pool no EntityManager). Mantém canvas/textura/material/sprite vivos —
 * é justamente isso que evita o ciclo de alocar e destruir textura de GPU a cada
 * jogador que entra ou sai. Só zera o cache de conteúdo, para o próximo
 * `drawLabel` redesenhar com o novo nome.
 */
export function resetDynamicLabel(lab) {
  lab._key = '';
  lab.sprite.visible = true;
  lab.sprite.renderOrder = 999;
}

/**
 * (Re)desenha o sprite com `text`. Cacheado por conteúdo (não redesenha o canvas
 * se o texto não mudou). Devolve o tamanho da pílula em unidades de mundo.
 */
export function drawLabel(lab, text, color = '#eafff2') {
  const key = `${text}|${color}`;
  if (lab._key !== key) {
    lab._key = key;
    render(lab, text, color);
  }
  return lab.sprite.userData.base;
}

function render(lab, text, color) {
  const m = measureLabel(text);
  const w = m.w;
  const h = m.h;

  // canvas: classe de tamanho, e SÓ CRESCE (nunca encolhe) — ver cabeçalho
  const canvas = lab.canvas;
  const wantW = Math.ceil(w / STEP) * STEP;
  const wantH = Math.ceil(h / STEP) * STEP;
  const grow = wantW > canvas.width || wantH > canvas.height;
  if (grow) {
    canvas.width = Math.max(wantW, canvas.width);
    canvas.height = Math.max(wantH, canvas.height);
  }
  const CW = canvas.width;
  const CH = canvas.height;

  const ctx = canvas.getContext('2d');
  if (!grow) ctx.clearRect(0, 0, CW, CH); // sem realocar: limpa o conteúdo anterior
  const x0 = (CW - w) / 2; // pílula CENTRADA (a sobra ao redor fica transparente)
  const y0 = (CH - h) / 2;
  paintLabel(ctx, x0, y0, m, color);

  // ao REDIMENSIONAR o canvas, o THREE não re-sobe a textura só com needsUpdate;
  // dispose() força a recriação no GPU com as novas dimensões.
  if (grow) lab.tex.dispose();
  lab.tex.needsUpdate = true;

  // RECORTE da pílula dentro do canvas: o quad passa a ser do tamanho exato da
  // pílula e a textura mostra só a região dela. O V é invertido porque a textura
  // de canvas sobe com flipY (a linha 0 do canvas vira v = 1).
  lab.tex.offset.set(x0 / CW, (CH - h - y0) / CH);
  lab.tex.repeat.set(w / CW, h / CH);
  lab.sprite.userData.base.x = w * LABEL_SCALE;
  lab.sprite.userData.base.y = h * LABEL_SCALE;
}

/**
 * Quebra `text` em no máximo `maxLines` linhas que cabem em `maxW` px — só em
 * ESPAÇOS. Ao esbarrar no teto, a última linha ganha reticências e o resto é
 * DESCARTADO ali mesmo: sair do laço na hora evita medir e montar as dezenas de
 * linhas que seriam jogadas fora (com 2.000 caracteres eram 166), e essa medição
 * roda a cada caractere digitado.
 */
function wrap(ctx, text, maxW, maxLines) {
  const words = String(text).split(' ');
  const lines = [];
  let cur = '';
  for (const wd of words) {
    const t = cur ? `${cur} ${wd}` : wd;
    if (ctx.measureText(t).width > maxW && cur) {
      // `cur` seria a última linha permitida E ainda sobra texto → corta aqui
      if (lines.length + 1 >= maxLines) { lines.push(ellipsize(ctx, cur, maxW)); return lines; }
      lines.push(cur);
      cur = wd;
    } else cur = t;
  }
  if (cur) lines.push(cur);
  return lines.length ? lines : [''];
}

/** `s` + reticências dentro de `maxW` px (tira caracteres do fim até caber). */
function ellipsize(ctx, s, maxW) {
  let t = s;
  while (t.length > 1 && ctx.measureText(t + ELLIPSIS).width > maxW) t = t.slice(0, -1);
  return t.replace(TRAILING_WS, '') + ELLIPSIS;
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
